"use client";

import { ADDRESS_LINE as FACTS_ADDRESS_LINE, COMPANY, OPENING_HOURS } from "@/lib/facts";
import type { CSSProperties, ReactNode } from "react";
import Link from "next/link";
import { Calendar, MapPin, Phone, PhoneCall, Send } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { PartnerBadges } from "@/components/PartnerBadges";

/* ─────────────────────────────────────────────────────────────
 * Gemeinsame Daten und Bausteine der Akquise-Landingpage.
 *
 * Kontaktdaten stehen EINMAL hier – Hero, Rückruf-Box, Sticky-Leiste
 * und Footer greifen darauf zu. Öffnungszeiten wie im Haupt-Footer und
 * in den Strukturdaten (layout.tsx).
 * ──────────────────────────────────────────────────────────── */
// Eine Quelle: src/lib/facts.ts (hier nur Re-Exporte für die Landingpage).
export const PHONE_DISPLAY: string = COMPANY.phoneDisplay;
export const PHONE_TEL: string = COMPANY.phoneTel;
export const PHONE_HOURS: string = OPENING_HOURS.short;
export const HOURS_LINES: string[] = [...OPENING_HOURS.display];
export const ADDRESS_LINE: string = FACTS_ADDRESS_LINE;

/** Woher der Besucher kommt – steuert Brücke, Kundenvorteil und Lead-Quelle. */
export type LandingReferrer = "pcboot" | null;

export type LandingLogo = { id: string; src: string; alt: string; href: string | null };

export type LandingTestimonial = {
  id: string;
  name: string;
  company: string;
  role: string;
  rating: number;
  text: string;
  avatarUrl: string | null;
};

export type LandingProject = {
  id: string;
  title: string;
  client: string | null;
  category: string;
  summary: string;
  description: string;
  cover: string | null;
  url: string | null;
  tags: string[];
  year: number | null;
  featured: boolean;
};

/** Terminart für das eingebettete Buchungs-Widget (Struktur wie in BookingFlow). */
export type LandingBookingType = {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  duration_options: number[];
  buffer_minutes: number;
};

/* ─────────────────────────────────────────────────────────────
 * Geschäftsführung – Daten wie auf /ueber-uns. Die drei Gründer leiten
 * das Team; Kunden rufen beim Unternehmen an, nicht bei einer Person.
 * ──────────────────────────────────────────────────────────── */
export const LEADERSHIP = [
  { name: "Tobias Fischer", title: "Gründer & Geschäftsführer", image: "/images/team/Tobias.webp", pcboot: false },
  { name: "Pascal Reininger", title: "Geschäftsführer", image: "/images/team/Pascal.webp", pcboot: true },
  { name: "Sebastian Erhard", title: "Geschäftsführer", image: "/images/team/Sebastian.webp", pcboot: true },
];

/** Hostname ohne „www.“ – für URL-Leisten in Mockups und Karten. */
export function prettyHost(url: string | null): string | null {
  if (!url) return null;
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
}

/* ─────────────────────────────────────────────────────────────
 * Sektions-Überschrift: Eyebrow, Titel, optionaler Untertitel.
 * ──────────────────────────────────────────────────────────── */
export function SectionHeading({
  eyebrow,
  title,
  text,
  align = "center",
}: {
  eyebrow: string;
  title: string;
  text?: string;
  align?: "center" | "left";
}) {
  return (
    <div className={align === "center" ? "mx-auto max-w-2xl text-center" : "max-w-2xl"}>
      <p className="text-sm font-semibold text-brand-500">{eyebrow}</p>
      <h2
        className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl"
        style={{ color: "var(--text)", letterSpacing: "-0.02em" }}
      >
        {title}
      </h2>
      {text && (
        <p className="mt-4 text-base leading-relaxed sm:text-lg" style={{ color: "var(--text-tertiary)" }}>
          {text}
        </p>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Link zur Terminbuchung. Zeigt er auf einen Anker der Seite (#termin),
 * rendern wir ein echtes <a>: nur dann löst der Browser ein
 * hashchange-Ereignis aus, auf das der Kontaktbereich den Reiter
 * „Termin wählen“ öffnet. Sonst next/link (z. B. /beratung).
 * ──────────────────────────────────────────────────────────── */
export function BookingLink({
  href,
  className,
  style,
  children,
}: {
  href: string;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}) {
  if (href.startsWith("#")) {
    return (
      <a href={href} className={className} style={style}>
        {children}
      </a>
    );
  }
  return (
    <Link href={href} className={className} style={style}>
      {children}
    </Link>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Mini-Header: nur Logo + ein Konversions-Button. Keine Navigation,
 * damit der Empfänger im Funnel bleibt.
 * ──────────────────────────────────────────────────────────── */
export function MiniHeader({ bookingHref = "/beratung" }: { bookingHref?: string }) {
  return (
    <header
      className="sticky top-0 z-40 border-b backdrop-blur-xl"
      style={{ backgroundColor: "var(--bg-header-glass)", borderColor: "var(--border)" }}
    >
      <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 lg:px-8">
        <Link href="/" aria-label="DMD Studio Startseite">
          <BrandLogo priority />
        </Link>
        <div className="flex items-center gap-3">
          <a
            href={`tel:${PHONE_TEL}`}
            className="hidden items-center gap-1.5 text-sm font-semibold transition-colors hover:text-brand-500 md:inline-flex"
            style={{ color: "var(--text-secondary)" }}
          >
            <PhoneCall className="h-4 w-4 text-brand-500" />
            {PHONE_DISPLAY}
          </a>
          <BookingLink
            href={bookingHref}
            className="hidden items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all duration-150 hover:bg-brand-600 hover:shadow-md sm:inline-flex"
          >
            <Calendar className="h-4 w-4" />
            Erstgespräch buchen
          </BookingLink>
        </div>
      </div>
    </header>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Mini-Footer: Pflichtangaben, Adresse, Telefon, Zeiten. Keine
 * Navigation, kein Newsletter.
 * ──────────────────────────────────────────────────────────── */
export function MiniFooter() {
  return (
    <footer className="border-t py-10" style={{ backgroundColor: "var(--bg-secondary)", borderColor: "var(--border)" }}>
      <div className="mx-auto max-w-6xl px-4 lg:px-8">
        <div className="grid gap-6 sm:grid-cols-2">
          <div>
            <BrandLogo width={120} height={36} className="h-9 w-auto" />
            <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--text-tertiary)" }}>
              DMD Studio GmbH – Full-Service Digitalagentur aus Unterfranken. Design, Marketing und
              Development aus einer Hand.
            </p>
            <div className="mt-4">
              <PartnerBadges />
            </div>
          </div>
          <div className="space-y-2 text-sm sm:text-right">
            <p className="inline-flex items-center gap-1.5" style={{ color: "var(--text-tertiary)" }}>
              <MapPin className="h-3.5 w-3.5" />
              {ADDRESS_LINE}
            </p>
            <p>
              <a
                href={`tel:${PHONE_TEL}`}
                className="inline-flex items-center gap-1.5 font-semibold transition-colors hover:text-brand-500"
                style={{ color: "var(--text-secondary)" }}
              >
                <Phone className="h-3.5 w-3.5" />
                {PHONE_DISPLAY}
              </a>
            </p>
            <div className="text-xs" style={{ color: "var(--text-quaternary)" }}>
              {HOURS_LINES.map((l) => (
                <p key={l}>{l}</p>
              ))}
            </div>
          </div>
        </div>
        <div
          className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t pt-4 text-xs"
          style={{ borderColor: "var(--border)", color: "var(--text-quaternary)" }}
        >
          <p>© {new Date().getFullYear()} DMD Studio GmbH</p>
          <div className="flex gap-4">
            <Link href="/impressum" className="hover:text-brand-500">
              Impressum
            </Link>
            <Link href="/datenschutz" className="hover:text-brand-500">
              Datenschutz
            </Link>
            <Link href="/agb" className="hover:text-brand-500">
              AGB
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Sticky-Leiste unten, nur mobil: Anrufen + Anfrage. Die Seite ist lang,
 * am Handy scrollt kaum jemand bis zum Formular.
 * ──────────────────────────────────────────────────────────── */
export function StickyMobileBar() {
  return (
    <div
      className="fixed inset-x-0 bottom-0 z-40 border-t px-3 pt-2 backdrop-blur-xl lg:hidden"
      style={{
        backgroundColor: "var(--bg-header-glass)",
        borderColor: "var(--border)",
        paddingBottom: "max(0.5rem, env(safe-area-inset-bottom))",
      }}
    >
      <div className="mx-auto flex max-w-lg gap-2">
        <a
          href={`tel:${PHONE_TEL}`}
          className="flex flex-1 items-center justify-center gap-2 rounded-lg border px-4 py-3 text-sm font-semibold"
          style={{ borderColor: "var(--border-secondary)", backgroundColor: "var(--card-bg)", color: "var(--text)" }}
        >
          <PhoneCall className="h-4 w-4 text-brand-500" />
          Anrufen
        </a>
        <a
          href="#anfrage"
          className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-brand-500 px-4 py-3 text-sm font-semibold text-white"
        >
          <Send className="h-4 w-4" />
          Anfrage
        </a>
      </div>
    </div>
  );
}
